/**
 * Vibejam 2026 portal hand-off contract.
 *
 * The Vibejam webring forwards continuity data through query params.
 * This module parses the current URL once at boot and exposes a
 * read-only snapshot. Everything is optional — only `portal === true`
 * means the player arrived via a portal (and the start portal must
 * appear). All other fields may be absent.
 */
export interface PortalContext {
  /** True when the URL carries `?portal=true` — the player arrived
   *  through another Vibejam game's exit portal. */
  readonly portal: boolean;
  /** URL of the game the player came from. Used as the destination
   *  when they walk into our start portal. */
  readonly ref: string | null;
  readonly username: string | null;
  /** Free-form colour: hex like `#ff00aa`, or a named CSS colour. */
  readonly color: string | null;
  readonly team: string | null;
  readonly avatarUrl: string | null;
  /** Health points in 1..100, or null. */
  readonly hp: number | null;
  /** Forward speed in m/s, or null. */
  readonly speed: number | null;
  readonly speedX: number | null;
  readonly speedY: number | null;
  readonly speedZ: number | null;
  readonly rotationX: number | null;
  readonly rotationY: number | null;
  readonly rotationZ: number | null;
  /** All raw params kept around so the start portal can forward them
   *  back to `ref` without dropping ones we don't model. */
  readonly raw: ReadonlyMap<string, string>;
}

const VIBEJAM_PORTAL_URL = 'https://vibejam.cc/portal/2026';

function asNumber(raw: string | null): number | null {
  if (raw === null || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** Parse the current URL into a PortalContext. Pure — call once at
 *  boot and pass the result around. */
export function readPortalContext(search: string = window.location.search): PortalContext {
  const params = new URLSearchParams(search);
  const raw = new Map<string, string>();
  for (const [k, v] of params) raw.set(k, v);

  return {
    portal: params.get('portal') === 'true',
    ref: params.get('ref'),
    username: params.get('username'),
    color: params.get('color'),
    team: params.get('team'),
    avatarUrl: params.get('avatar_url'),
    hp: asNumber(params.get('hp')),
    speed: asNumber(params.get('speed')),
    speedX: asNumber(params.get('speed_x')),
    speedY: asNumber(params.get('speed_y')),
    speedZ: asNumber(params.get('speed_z')),
    rotationX: asNumber(params.get('rotation_x')),
    rotationY: asNumber(params.get('rotation_y')),
    rotationZ: asNumber(params.get('rotation_z')),
    raw
  };
}

/**
 * Build the URL the start portal should redirect to — sends the
 * player back to the originating game (`ref`). Forwards every param
 * the player arrived with except `ref` itself, which becomes the base
 * URL.
 */
export function buildReturnUrl(ctx: PortalContext): string | null {
  if (!ctx.ref) return null;
  let base = ctx.ref;
  if (!base.startsWith('http://') && !base.startsWith('https://')) {
    base = 'https://' + base;
  }
  const out = new URLSearchParams();
  for (const [key, value] of ctx.raw) {
    if (key === 'ref') continue;
    out.append(key, value);
  }
  const tail = out.toString();
  return tail ? `${base}?${tail}` : base;
}

/**
 * Build the URL the exit portal should redirect to — hands the player
 * off to the Vibejam 2026 webring with continuity data filled in from
 * live game state. `ref` is wired to our own host so the next game can
 * portal them back to us.
 */
export function buildVibejamExitUrl(opts: {
  readonly username?: string | null;
  readonly color?: string | null;
  readonly speed?: number | null;
  readonly extra?: ReadonlyMap<string, string>;
}): string {
  const params = new URLSearchParams();
  params.set('portal', 'true');
  // ref points to whatever host actually serves the game — works for
  // any deploy without us having to hardcode a URL.
  params.set('ref', window.location.host);
  if (opts.username) params.set('username', opts.username);
  if (opts.color) params.set('color', opts.color);
  if (typeof opts.speed === 'number' && Number.isFinite(opts.speed)) {
    params.set('speed', opts.speed.toFixed(2));
  }
  if (opts.extra) {
    for (const [key, value] of opts.extra) {
      // Don't let extras stomp the keys we just set (portal/ref).
      if (params.has(key)) continue;
      params.set(key, value);
    }
  }
  return `${VIBEJAM_PORTAL_URL}?${params.toString()}`;
}
